import { Thermometer, Zap, Droplets, Activity } from "lucide-react";
import type { Device } from "./FloorPlanUpload";
import { cn } from "../../../utils/utils";
import { useGetLiveData } from "../../../queries/admin";

interface DevicePopupProps {
  device: Device;
}

export function DevicePopup({ device }: DevicePopupProps) {
  const { data: rawData } = useGetLiveData(device.id);
  const x = device.x;
  const y = device.y;
  const getStatusBadge = (status: Device["status"]) => {
    switch (status) {
      case "active":
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Active
          </div>
        );
      case "warning":
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            Warning
          </div>
        );
      case "critical":
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            Critical
          </div>
        );
      case "inactive":
      default:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200">
            <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            Inactive
          </div>
        );
    }
  };

  const getIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "temperature":
        return <Thermometer className="h-4 w-4" />;
      case "voltage":
        return <Zap className="h-4 w-4" />;
      case "humidity":
        return <Droplets className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const formatValue = (key: string, value: any) => {
    if (typeof value === "number") {
      switch (key) {
        case "temperature":
          return `${value}°C`;
        case "voltage":
          return `${value}V`;
        case "humidity":
          return `${value}%`;
        default:
          return value.toString();
      }
    }
    return value;
  };

  // Position the popup to avoid edges
  const popupStyle = {
    left: x > 70 ? "auto" : `${x + 5}%`,
    right: x > 70 ? `${100 - x + 5}%` : "auto",
    top: y > 70 ? "auto" : `${y + 5}%`,
    bottom: y > 70 ? `${100 - y + 5}%` : "auto",
  };

  if (!rawData) {
    return null;
  }
  const { id, timestamp, deviceId, createdAt, updatedAt, ...deviceData } =
    rawData;

  return (
    <div
      className={cn(
        "absolute z-50 w-72 bg-white rounded-xl border border-gray-200 shadow-lg",
        "animate-in fade-in-50 zoom-in-95 duration-300 overflow-hidden",
      )}
      style={popupStyle}
    >
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-blue-50 rounded-lg">
              <Activity className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {device.name}
              </h4>
              <p className="text-xs text-gray-500">Device Details</p>
            </div>
          </div>
          {getStatusBadge(device.status)}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        <div className="space-y-3">
          {Object.entries(deviceData).map(([key, value], index) => (
            <div
              key={key}
              className="flex items-center justify-between p-2 rounded-lg bg-gray-50/50 hover:bg-gray-100/50 transition-all duration-200"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-center space-x-3">
                <div className="p-1.5 bg-white rounded-lg border border-gray-200">
                  {getIcon(key)}
                </div>
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {key}
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-gray-900">
                  {formatValue(key, value)}
                </span>
                {key === "status" && (
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-600">Live</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                Click device for full details
              </span>
              <div className="flex items-center gap-1 text-green-600">
                <Activity className="h-3 w-3" />
                <span className="font-medium">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
